import React from 'react';
import { Card, Typography, Row, Col } from 'antd';
import { useHistory } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Internal linking strategy for SEO and user experience
const linkingStrategy = {
  // Blog post to product linking
  'emotional-support-stuffed-animals-for-kids': [
    { type: 'product', id: 'lumi-classic-bear', title: 'Lumi Classic Bear', reason: 'perfect emotional support companion' },
    { type: 'product', id: 'lumi-gentle', title: 'Lumi Gentle', reason: 'designed for sensitive children' }
  ],
  'anxiety-support-toys': [
    { type: 'product', id: 'lumi-gentle', title: 'Lumi Gentle', reason: 'specialized anxiety support' },
    { type: 'blog', slug: 'sensory-comfort-bear-for-anxious-children', title: 'Sensory Comfort Bears', reason: 'specialized anxiety solutions' }
  ],
  'bedtime-routine-helpers': [
    { type: 'product', id: 'lumi-night', title: 'Lumi Night', reason: 'bedtime-specific features' },
    { type: 'blog', slug: 'calming-stuffed-animal-for-bedtime-routines', title: 'Bedtime Routine Guide', reason: 'comprehensive bedtime strategies' }
  ],
  // Product to blog linking
  'lumi-classic-bear': [
    { type: 'blog', slug: 'emotional-support-stuffed-animals-for-kids', title: 'Emotional Support Guide', reason: 'understanding emotional support benefits' },
    { type: 'blog', slug: 'wellness-companion-teddy-bear-for-children', title: 'Wellness Companion Benefits', reason: 'daily wellness support' }
  ],
  'lumi-gentle': [
    { type: 'blog', slug: 'gentle-wellness-plush-for-young-children', title: 'Gentle Wellness Guide', reason: 'age-appropriate wellness' },
    { type: 'blog', slug: 'sensory-comfort-bear-for-anxious-children', title: 'Anxiety Support Guide', reason: 'managing childhood anxiety' }
  ]
};

// Related content suggestions based on categories and tags
const getRelatedContent = (currentSlug, currentTags = [], currentCategory = '') => {
  const suggestions = [];
  
  // Category-based suggestions
  const categoryLinks = {
    'Emotional Wellness': [
      { type: 'blog', slug: 'emotional-support-stuffed-animals-for-kids', title: 'Emotional Support Stuffed Animals' },
      { type: 'blog', slug: 'emotional-regulation-bears-for-children', title: 'Emotional Regulation Bears' }
    ],
    'Sleep & Bedtime': [
      { type: 'blog', slug: 'calming-stuffed-animal-for-bedtime-routines', title: 'Calming Bedtime Routines' },
      { type: 'product', id: 'lumi-night', title: 'Lumi Night' }
    ],
    'Anxiety & Emotional Support': [
      { type: 'blog', slug: 'sensory-comfort-bear-for-anxious-children', title: 'Sensory Comfort Bears' },
      { type: 'product', id: 'lumi-gentle', title: 'Lumi Gentle' }
    ],
    'Mindfulness & Wellness': [
      { type: 'blog', slug: 'mindfulness-plush-toys-for-kids', title: 'Mindfulness Plush Toys' },
      { type: 'blog', slug: 'wellness-focused-comfort-toys', title: 'Wellness-Focused Comfort Toys' }
    ]
  };

  // Tag-based suggestions
  const tagLinks = {
    'emotional support stuffed animals': [
      { type: 'blog', slug: 'emotional-support-stuffed-animals-for-kids', title: 'Complete Emotional Support Guide' }
    ],
    'anxiety management': [
      { type: 'blog', slug: 'sensory-comfort-bear-for-anxious-children', title: 'Anxiety Support Solutions' }
    ],
    'bedtime routines': [
      { type: 'blog', slug: 'calming-stuffed-animal-for-bedtime-routines', title: 'Bedtime Routine Transformation' }
    ],
    'emotional regulation': [
      { type: 'blog', slug: 'emotional-regulation-bears-for-children', title: 'Building Self-Control Skills' }
    ]
  };

  // Add category-based suggestions
  if (currentCategory && categoryLinks[currentCategory]) {
    suggestions.push(...categoryLinks[currentCategory].filter(link => 
      (link.type === 'blog' && link.slug !== currentSlug) || link.type === 'product'
    ));
  }

  // Add tag-based suggestions
  currentTags.forEach(tag => {
    if (tagLinks[tag]) {
      suggestions.push(...tagLinks[tag].filter(link => 
        (link.type === 'blog' && link.slug !== currentSlug) || link.type === 'product'
      ));
    }
  });

  // Remove duplicates
  return suggestions.filter((item, index, self) => 
    index === self.findIndex(t => t.type === item.type && (t.slug === item.slug || t.id === item.id))
  ).slice(0, 4);
};

const InternalLinks = ({ 
  currentSlug, 
  currentTags = [], 
  currentCategory = '', 
  title = "Related Resources",
  maxItems = 4 
}) => {
  const history = useHistory();

  // Get specific strategy links if available
  const strategyLinks = linkingStrategy[currentSlug] || [];
  
  // Get related content suggestions
  const relatedLinks = getRelatedContent(currentSlug, currentTags, currentCategory);
  
  // Combine and limit results
  const allLinks = [...strategyLinks, ...relatedLinks]
    .filter((item, index, self) => 
      index === self.findIndex(t => t.type === item.type && (t.slug === item.slug || t.id === item.id))
    )
    .slice(0, maxItems);

  if (allLinks.length === 0) return null;

  const handleLinkClick = (link) => {
    if (link.type === 'blog') {
      history.push(`/blog/${link.slug}`);
    } else if (link.type === 'product') {
      history.push(`/products/${link.id}`);
    }
  };

  return (
    <div style={{ margin: '40px 0' }}>
      <h3 style={{ 
        color: '#667eea', 
        fontWeight: 600, 
        marginBottom: '24px',
        borderBottom: '2px solid #667eea',
        paddingBottom: '8px',
        display: 'inline-block'
      }}>
        {title}
      </h3>
      
      <Row gutter={[16, 16]}>
        {allLinks.map((link, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card
              hoverable
              size="small"
              onClick={() => handleLinkClick(link)}
              style={{
                borderRadius: '8px',
                border: '1px solid #e8f4fd',
                backgroundColor: '#fafbff',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              bodyStyle={{ padding: '16px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, marginRight: '8px' }}>
                  <Text strong style={{ 
                    color: '#667eea', 
                    fontSize: '14px',
                    lineHeight: '1.4',
                    display: 'block',
                    marginBottom: '4px'
                  }}>
                    {link.title}
                  </Text>
                  {link.reason && (
                    <Text style={{ 
                      fontSize: '12px', 
                      color: '#8c8c8c',
                      lineHeight: '1.3'
                    }}>
                      {link.reason}
                    </Text>
                  )}
                  <div style={{ marginTop: '8px' }}>
                    <Text style={{ 
                      fontSize: '11px', 
                      color: '#667eea',
                      textTransform: 'uppercase',
                      fontWeight: 500
                    }}>
                      {link.type === 'blog' ? 'Guide' : 'Product'}
                    </Text>
                  </div>
                </div>
                <ArrowRightOutlined style={{ 
                  color: '#667eea', 
                  fontSize: '12px',
                  flexShrink: 0
                }} />
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default InternalLinks;